/**
 * kelp-hero.js — the living kelp counter at the foot of the Kelp offer page.
 *
 * The page itself scrolls over a plain, static backdrop (kelp-hero-reef). All
 * the motion is concentrated in one bounded canvas at the bottom — the finale —
 * and nothing there runs until the reader arrives. Jobs:
 *
 *   1. Build the kelp forest, the fish and the drifting motes into the finale
 *      canvas. Deterministic pseudo-random geometry — same reef every load.
 *   2. When the finale scrolls into view, grow it once: a single eased pass of
 *      the stage's own `--kelp-g` (0 → 1) that every layer reads, plus the
 *      sway / swim / drift animations (paused in CSS until `.is-live`). Off
 *      screen the animations pause again, so the page goes idle either side.
 *   3. Reveal chapters as they enter, and light the chapter rail alongside them.
 *   4. Pull live impact totals from the Greenspark API into every
 *      `[data-kelp-metric]` / `[data-kelp-progress]` element, counting up on view.
 *
 * prefers-reduced-motion is handled in CSS (`[data-kelp-motion]` cancelled): the
 * forest is shown fully grown at once, counters snap, nothing eases.
 *
 * Budget devices get a lite reef (`data-kelp-lite` on the root): a smaller
 * population, and growth by transform + fade instead of the clip reveal.
 */

(() => {
  const root = document.querySelector('[data-kelp-hero]');
  if (!root) return;

  const finale = root.querySelector('[data-kelp-finale]');
  const stage = finale && finale.querySelector('[data-kelp-stage]');
  const forest = finale && finale.querySelector('[data-kelp-forest]');
  const school = finale && finale.querySelector('[data-kelp-school]');
  const motes = finale && finale.querySelector('[data-kelp-motes]');
  const badge = root.querySelector('[data-kelp-badge]');
  const rail = root.querySelector('.kelp-hero__rail');
  const leaf = root.querySelector('[data-kelp-leaf]');

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Budget-device heuristic: little RAM (Chromium-only signal), few cores, or a
     phone-width viewport. The viewport check matters because many phones report
     8 cores yet can't afford the full reef (and desktops lie the other way), so
     a narrow screen always gets the lighter population. Missing signals count as
     capable, so unknown wide browsers still get the full reef. */
  const lite =
    (navigator.deviceMemory || 8) <= 4 ||
    (navigator.hardwareConcurrency || 8) <= 4 ||
    window.matchMedia('(max-width: 699px)').matches;

  const FROND_COUNT = lite ? 12 : 22;
  const FISH_COUNT = lite ? 4 : 7;
  const MOTE_COUNT = lite ? 12 : 20;
  const PULSE_INTERVAL = 21000;

  /* Deterministic hash-noise in [0,1). Seeded by index so the reef is stable. */
  const rnd = (seed) => {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  };

  /**
   * Three depth bands. Band 1 sits furthest back (small, muted), band 3 closest
   * (tall, brass-lit). `start` staggers when the band begins to grow so the
   * forest fills in from the back. Depth is carried by size, colour and opacity
   * — all compositor-cheap. Each frond is one inline SVG: a curved bezier stipe
   * and teardrop leaf blades, sizes below in the 100×300 viewBox's own units.
   */
  const BANDS = [
    {
      height: [30, 44],
      width: 62,
      bottom: '13%',
      depth: 1,
      start: 0,
      stroke: 4,
      stipe: '#3a4a24',
      leaf: '#42501f',
      bladeLen: 46,
      bladeCount: 8,
      opacity: [0.34, 0.18],
    },
    {
      height: [46, 62],
      width: 82,
      bottom: '6%',
      depth: 2,
      start: 0.05,
      stroke: 5,
      stipe: '#6d6b25',
      leaf: '#7d7a2c',
      bladeLen: 58,
      bladeCount: 10,
      opacity: [0.6, 0.24],
    },
    {
      height: [64, 84],
      width: 104,
      bottom: '0%',
      depth: 3,
      start: 0.1,
      stroke: 6,
      stipe: '#b08a2c',
      leaf: '#c99a34',
      bladeLen: 72,
      bladeCount: 13,
      opacity: [0.82, 0.16],
    },
  ];

  /* Cubic bezier helpers, in the frond's 100×300 viewBox coordinates. */
  const SVGNS = 'http://www.w3.org/2000/svg';
  const cubic = (p, t) => {
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    return {
      x: a * p[0].x + b * p[1].x + c * p[2].x + d * p[3].x,
      y: a * p[0].y + b * p[1].y + c * p[2].y + d * p[3].y,
    };
  };
  const cubicTangent = (p, t) => {
    const u = 1 - t;
    const a = 3 * u * u;
    const b = 6 * u * t;
    const c = 3 * t * t;
    return {
      x: a * (p[1].x - p[0].x) + b * (p[2].x - p[1].x) + c * (p[3].x - p[2].x),
      y: a * (p[1].y - p[0].y) + b * (p[2].y - p[1].y) + c * (p[3].y - p[2].y),
    };
  };

  const FISH_TONES = [
    ['#e6ddc8', '#b3a98e'],
    ['#e3c98f', '#b8894b'],
    ['#cfe0d2', '#8fa898'],
    ['#f0d9a8', '#c4a484'],
  ];

  const buildFronds = () => {
    const frag = document.createDocumentFragment();

    for (let i = 0; i < FROND_COUNT; i += 1) {
      const band = BANDS[i % BANDS.length];
      const across = i / (FROND_COUNT - 1);

      /* Each frond starts early and finishes over most of the grow pass, so
         none snaps in. `from` = --kelp-g at which this stipe begins to draw,
         `span` = how much of --kelp-g it takes to reach full height. */
      const fromN = band.start + 0.22 * (0.45 * across + 0.55 * rnd(i + 17));
      const spanN = 0.62 + rnd(i + 41) * 0.3;
      const grown = `clamp(0,calc((var(--kelp-g) - ${fromN.toFixed(3)}) / ${spanN.toFixed(3)}),1)`;

      const frond = document.createElement('div');
      frond.className = 'kelp-hero__frond';
      /* Inset well inside the canvas (9%..91%) so sway can never clip an edge. */
      const style = [
        `left:${(9 + across * 82 + (rnd(i) - 0.5) * 3).toFixed(1)}%`,
        `bottom:${band.bottom}`,
        `width:${band.width}px`,
        `height:${(band.height[0] + rnd(i + 9) * (band.height[1] - band.height[0])).toFixed(1)}%`,
        `z-index:${band.depth}`,
        `opacity:clamp(0,calc((var(--kelp-g) - ${fromN.toFixed(3)}) / 0.1),1)`,
      ];
      frond.style.cssText = style.join(';');

      const holdfast = document.createElement('span');
      holdfast.className = 'kelp-hero__frond-holdfast';

      /* ── The stipe: one S-curved cubic in a 100×300 (w×h) viewBox ─────────── */
      const tipWander = (rnd(i + 31) - 0.5) * 56;
      const bend1 = (rnd(i + 37) - 0.5) * 60;
      const bend2 = (rnd(i + 43) - 0.5) * 66;
      const P = [
        { x: 50, y: 300 },
        { x: 50 + bend1, y: 206 },
        { x: 50 + tipWander * 0.5 + bend2, y: 96 },
        { x: 50 + tipWander, y: 14 },
      ];
      const d = `M${P[0].x},${P[0].y} C${P[1].x.toFixed(1)},${P[1].y} ${P[2].x.toFixed(1)},${P[2].y} ${P[3].x.toFixed(1)},${P[3].y}`;

      /* Arc-length lookup for this stipe, in viewBox units. The stroke draws by
         arc length (dasharray on pathLength=1), but blades are placed by bezier
         parameter t — and on an S-curve the two diverge. Keying a blade's reveal
         to its arc fraction (not t) makes each blade appear exactly as the draw
         reaches it, instead of popping in ahead of the stroke and floating
         detached above the tip. */
      const ARC_SAMPLES = 24;
      const arcCum = [0];
      let arcPrev = cubic(P, 0);
      for (let s = 1; s <= ARC_SAMPLES; s += 1) {
        const pt = cubic(P, s / ARC_SAMPLES);
        arcCum.push(arcCum[s - 1] + Math.hypot(pt.x - arcPrev.x, pt.y - arcPrev.y));
        arcPrev = pt;
      }
      const arcTotal = arcCum[ARC_SAMPLES] || 1;
      const arcFrac = (t) => {
        const f = Math.max(0, Math.min(1, t)) * ARC_SAMPLES;
        const k = Math.min(ARC_SAMPLES - 1, Math.floor(f));
        return (arcCum[k] + (arcCum[k + 1] - arcCum[k]) * (f - k)) / arcTotal;
      };

      const svg = document.createElementNS(SVGNS, 'svg');
      svg.setAttribute('class', 'kelp-hero__frond-svg');
      svg.setAttribute('viewBox', '0 0 100 300');
      /* Fill the frond's box in both axes — the drawing is meant to stretch to
         the band's width and height, not letterbox. The stroke width then scales
         with the box, which is fine (kelp is near-vertical). We must NOT use
         vector-effect:non-scaling-stroke here: combined with this non-uniform
         scale and the pathLength=1 dash it corrupts the draw-on, leaving stray
         painted stipe segments floating near the tip. */
      svg.setAttribute('preserveAspectRatio', 'none');

      const sway = document.createElementNS(SVGNS, 'g');
      sway.setAttribute('class', 'kelp-hero__frond-sway');
      sway.setAttribute('data-kelp-motion', '');
      /* One compositor transform, no redraw loop. Amplitude shrinks for the
         short back band so distant kelp barely stirs. */
      sway.style.animation =
        `kelp-hero-sway ${(3.4 + rnd(i + 3) * 2.8).toFixed(1)}s ease-in-out ` +
        `-${(rnd(i + 5) * 3).toFixed(2)}s infinite alternate`;

      const stipe = document.createElementNS(SVGNS, 'path');
      stipe.setAttribute('class', 'kelp-hero__stipe');
      stipe.setAttribute('d', d);
      stipe.setAttribute('pathLength', '1');
      /* Grow = draw the stroke on. dasharray 1 + offset (1 → 0) reveals the
         stipe from holdfast to tip as --kelp-g climbs, then holds. */
      stipe.style.cssText = [
        `stroke:${band.stipe}`,
        `stroke-width:${band.stroke}px`,
        `stroke-dashoffset:calc(1 - ${grown})`,
      ].join(';');
      sway.appendChild(stipe);

      /* ── Blades: teardrop leaves along the stipe, fading in as the draw
         passes each one ────────────────────────────────────────────────────── */
      for (let j = 0; j < band.bladeCount; j += 1) {
        const t = 0.16 + (j / (band.bladeCount - 1)) * 0.78;
        const at = cubic(P, t);
        const tan = cubicTangent(P, t);
        const tanAngle = (Math.atan2(tan.y, tan.x) * 180) / Math.PI;
        const side = j % 2 === 0 ? 1 : -1;
        /* Blades stream up along the stipe (giant-kelp blades float toward the
           surface), fanning slightly to each side — not stiff and perpendicular. */
        const angle = tanAngle + side * (22 + rnd(i * 9 + j) * 20);
        /* Longest mid-frond, tapering at holdfast and tip. */
        const len = band.bladeLen * (0.55 + 0.7 * Math.sin(t * Math.PI)) * (0.85 + rnd(i * 7 + j) * 0.5);
        const w = len * (0.12 + rnd(i + j * 3) * 0.05);
        const leaf =
          `M0,0 C${(len * 0.28).toFixed(1)},${(-w).toFixed(1)} ${(len * 0.8).toFixed(1)},${(-w * 0.7).toFixed(1)} ` +
          `${len.toFixed(1)},0 C${(len * 0.72).toFixed(1)},${(w * 0.6).toFixed(1)} ${(len * 0.32).toFixed(1)},${(w * 0.5).toFixed(1)} 0,0 Z`;

        /* Reveal when the stroke's arc-length draw has actually reached this
           blade: --kelp-g ≈ from + arcFrac(t)·span (arc fraction, not raw t). */
        const thresh = (fromN + arcFrac(t) * spanN).toFixed(3);
        const op = (band.opacity[0] + rnd(i * 11 + j) * band.opacity[1]).toFixed(2);

        const blade = document.createElementNS(SVGNS, 'path');
        blade.setAttribute('class', 'kelp-hero__blade');
        blade.setAttribute('d', leaf);
        blade.setAttribute('transform', `translate(${at.x.toFixed(1)},${at.y.toFixed(1)}) rotate(${angle.toFixed(1)})`);
        blade.style.cssText = [
          `fill:${band.leaf}`,
          `opacity:calc(${op} * clamp(0,calc((var(--kelp-g) - ${thresh}) / 0.05),1))`,
        ].join(';');
        sway.appendChild(blade);
      }

      svg.appendChild(sway);
      frond.append(holdfast, svg);
      frag.appendChild(frond);
    }

    forest.appendChild(frag);
  };

  const buildFish = () => {
    const frag = document.createDocumentFragment();

    for (let i = 0; i < FISH_COUNT; i += 1) {
      const [light, dark] = FISH_TONES[i % FISH_TONES.length];
      const distant = i % 3 === 0;
      const width = distant ? 9 + rnd(i) * 4 : 14 + rnd(i) * 12;

      const fish = document.createElement('div');
      fish.className = 'kelp-hero__fish';
      fish.setAttribute('data-kelp-motion-hide', '');
      fish.style.top = `${(8 + rnd(i + 7) * 52).toFixed(1)}%`;

      const swim = document.createElement('div');
      swim.setAttribute('data-kelp-motion', '');
      swim.style.animation =
        `${i % 2 === 0 ? 'kelp-hero-swim-right' : 'kelp-hero-swim-left'} ` +
        `${(14 + rnd(i + 11) * 16).toFixed(1)}s linear -${(rnd(i + 13) * 22).toFixed(1)}s infinite`;

      const bob = document.createElement('div');
      bob.className = 'kelp-hero__fish-bob';
      bob.setAttribute('data-kelp-motion', '');
      bob.style.animation = `kelp-hero-bob ${(1.6 + rnd(i + 17) * 1.6).toFixed(1)}s ease-in-out infinite alternate`;

      const body = document.createElement('div');
      body.className = 'kelp-hero__fish-body';
      body.style.cssText = [
        `width:${width.toFixed(0)}px`,
        `height:${(width * 0.46).toFixed(0)}px`,
        `background:linear-gradient(160deg,${light},${dark})`,
      ].join(';');

      const tail = document.createElement('span');
      tail.className = 'kelp-hero__fish-tail';
      tail.style.background = dark;

      bob.append(body, tail);
      swim.appendChild(bob);
      fish.appendChild(swim);
      frag.appendChild(fish);
    }

    school.appendChild(frag);
  };

  const buildMotes = () => {
    const frag = document.createDocumentFragment();
    const step = 96 / MOTE_COUNT;

    for (let i = 0; i < MOTE_COUNT; i += 1) {
      const size = `${(2 + rnd(i + 71) * 3).toFixed(1)}px`;

      /* Gate each bubble on growth: the first third are always up (thresh 0),
         the rest fade in across --kelp-g so the water fills with bubbles as the
         forest grows and is thickest at the goal. Reveal over a 0.2 window. */
      const thresh = (Math.max(0, i / MOTE_COUNT - 0.33) * 1.35).toFixed(3);

      const mote = document.createElement('span');
      mote.className = 'kelp-hero__mote';
      mote.setAttribute('data-kelp-motion-hide', '');
      mote.style.cssText = [
        `left:${(3 + i * step + rnd(i + 51) * 4).toFixed(1)}%`,
        `bottom:${(4 + rnd(i + 61) * 24).toFixed(1)}%`,
        `width:${size}`,
        `height:${size}`,
        `opacity:clamp(0,calc((var(--kelp-g) - ${thresh}) / 0.2),1)`,
      ].join(';');

      const drift = document.createElement('span');
      drift.className = 'kelp-hero__mote-drift';
      drift.setAttribute('data-kelp-motion', '');
      drift.style.animation =
        `kelp-hero-rise ${(8 + rnd(i + 81) * 8).toFixed(1)}s linear ` +
        `-${(rnd(i + 91) * 12).toFixed(1)}s infinite`;

      mote.appendChild(drift);
      frag.appendChild(mote);
    }

    motes.appendChild(frag);
  };

  /**
   * The finale's growth = progress toward the goal. `--kelp-g` runs 0 → 1 where
   * 1 is the full 15,000-kelp forest, so the reader always sees the forest at
   * exactly `funded ÷ goal` — the drawing *is* the count. On first scroll-in it
   * eases up from zero to the current level; two controls make it interactive:
   *
   *   • Replay    → drops to zero and grows back to where we are now.
   *   • See goal  → grows on to the full forest (1) and shows the 15,000 figure.
   *
   * `.is-live` gates the sway / swim / drift animations to on-screen only (inline
   * play-state, since an inline `animation` shorthand outranks any stylesheet
   * rule). reduced-motion snaps to the current level with no easing.
   *
   * Dev only: append `?kelpdev` to the URL for a slider that scrubs --kelp-g so
   * we can tune the look together. Remove startDevSlider + its call when done.
   */
  const startFinale = () => {
    if (!stage) return;

    const metric = finale.querySelector('[data-kelp-metric]');
    const bar = finale.querySelector('[data-kelp-progress]');
    const goal = (bar && parseFloat(bar.getAttribute('data-kelp-goal'))) || 25000;
    const funded = () => {
      if (metric && metric.kelpTarget != null) return metric.kelpTarget;
      const attr = metric && parseFloat(metric.getAttribute('data-kelp-target'));
      return attr || 0;
    };
    const progress = () => Math.max(0, Math.min(1, funded() / goal));

    let g = 0;
    let raf = null;
    const setG = (v) => {
      g = v;
      stage.style.setProperty('--kelp-g', v.toFixed(3));
    };
    const cancel = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = null;
      }
    };
    /* Eases g from wherever it is now to a target read fresh every frame — so a
       Greenspark figure landing mid-glide simply retargets the animation. */
    const glide = (getTarget, duration) => {
      cancel();
      if (reduced) {
        setG(getTarget());
        return;
      }
      let t0 = null;
      const from = g;
      const ease = (t) => 1 - (1 - t) ** 3;
      const step = (now) => {
        if (t0 === null) t0 = now;
        const t = Math.min(1, (now - t0) / duration);
        setG(from + (getTarget() - from) * ease(t));
        if (t < 1) raf = window.requestAnimationFrame(step);
        else {
          setG(getTarget());
          raf = null;
        }
      };
      raf = window.requestAnimationFrame(step);
    };

    /* An inline `animation` shorthand resets animation-play-state to running and
       outranks any stylesheet rule, so gating has to be inline too. */
    const motionNodes = Array.from(finale.querySelectorAll('[data-kelp-motion]'));
    const setPlay = (state) => {
      motionNodes.forEach((el) => {
        el.style.animationPlayState = state;
      });
    };

    const setBar = (ratio) => {
      if (bar) bar.style.width = `${(ratio * 100).toFixed(2)}%`;
    };

    /* ── Interactive controls ─────────────────────────────────────────────── */
    /* Scope to <button>: the progress fill also carries a `data-kelp-goal`
       attribute (its numeric goal), so a bare [data-kelp-goal] would match it. */
    const replayBtn = finale.querySelector('button[data-kelp-replay]');
    const goalBtn = finale.querySelector('button[data-kelp-goal]');

    const toCurrent = () => {
      finale.classList.remove('is-goal');
      setPlay('running');
      setG(0);
      setBar(0);
      if (metric && metric.kelpReplay) metric.kelpReplay();
      setBar(progress());
      glide(progress, 2600);
    };
    const toGoal = () => {
      finale.classList.add('is-goal');
      setPlay('running');
      if (metric && metric.kelpShow) metric.kelpShow(goal);
      setBar(1);
      glide(() => 1, 2200);
    };
    if (replayBtn) replayBtn.addEventListener('click', toCurrent);
    if (goalBtn) goalBtn.addEventListener('click', toGoal);

    const dev = new URLSearchParams(window.location.search).has('kelpdev');
    if (dev) startDevSlider(setG, () => g, progress, goal, funded);

    if (reduced) {
      setG(progress());
      return;
    }

    setPlay('paused');
    let grownOnce = false;
    const growIn = () => glide(progress, 2600);

    if (dev) {
      finale.classList.add('is-live');
      setPlay('running');
      return; // slider drives --kelp-g; skip the auto-grow
    }

    if (!('IntersectionObserver' in window)) {
      finale.classList.add('is-live');
      setPlay('running');
      growIn();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          finale.classList.toggle('is-live', entry.isIntersecting);
          setPlay(entry.isIntersecting ? 'running' : 'paused');
          if (entry.isIntersecting && !grownOnce) {
            grownOnce = true;
            growIn();
          }
        });
      },
      { threshold: 0.12 },
    );

    observer.observe(stage);
  };

  /* Dev-only growth scrubber. Behind `?kelpdev`; delete this and its single call
     in startFinale once the look is dialled in. */
  const startDevSlider = (setG, getG, progress, goal) => {
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'position:fixed;left:12px;bottom:12px;z-index:99999;width:230px;padding:10px 12px;' +
      'border-radius:10px;background:rgba(0,0,0,0.82);color:#e8d3a3;' +
      "font:12px/1.4 system-ui,sans-serif;pointer-events:auto;";
    const label = document.createElement('div');
    label.style.marginBottom = '6px';
    const input = document.createElement('input');
    input.type = 'range';
    input.min = '0';
    input.max = '1';
    input.step = '0.01';
    input.style.width = '100%';

    const init = progress();
    input.value = String(init);
    const render = () => {
      label.textContent = `--kelp-g ${getG().toFixed(2)} · current ${Math.round(progress() * 100)}% · goal ${goal.toLocaleString('en-US')}`;
    };
    setG(init);
    render();
    input.addEventListener('input', () => {
      setG(parseFloat(input.value));
      render();
    });
    wrap.append(label, input);
    document.body.appendChild(wrap);
  };

  /* Reveal each block once, on entry. */
  const startReveals = () => {
    const blocks = Array.from(root.querySelectorAll('[data-kelp-reveal]'));
    const show = (el) => el.setAttribute('data-kelp-reveal', 'shown');

    if (reduced || !('IntersectionObserver' in window)) {
      blocks.forEach(show);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
    );

    blocks.forEach((block) => observer.observe(block));
  };

  /* Chapter rail: the dot for the chapter in view grows and turns brass. */
  const startRail = () => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const dot = root.querySelector(
            `[data-kelp-rail-dot="${entry.target.getAttribute('data-kelp-chapter')}"]`,
          );
          if (dot) dot.classList.toggle('is-active', entry.isIntersecting);
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 },
    );

    root.querySelectorAll('[data-kelp-chapter]').forEach((chapter) => observer.observe(chapter));
  };

  /**
   * Fixed chrome visibility. The badge shows once you are past the top and hides
   * at the very bottom; the rail hides at the very bottom too. Event-driven off
   * a passive scroll listener, rAF-coalesced — no continuous loop, and it writes
   * only when a threshold is actually crossed.
   */
  const startChrome = () => {
    if (!badge && !rail) return;

    let queued = false;
    let lastVisible = null;
    let lastHidden = null;

    const apply = () => {
      queued = false;
      const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const raw = Math.min(1, Math.max(0, window.scrollY / scrollable));

      const visible = raw > 0.04 && raw < 0.97;
      const hidden = raw >= 0.97;
      if (badge && visible !== lastVisible) {
        lastVisible = visible;
        badge.classList.toggle('is-visible', visible);
      }
      if (rail && hidden !== lastHidden) {
        lastHidden = hidden;
        rail.classList.toggle('is-hidden', hidden);
      }
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  };

  /**
   * Live impact numbers. Each `[data-kelp-metric]` element ships a hardcoded
   * `data-kelp-target` fallback, counts up to it when it enters view, and is
   * retargeted once the Greenspark payload arrives. Metric grammar:
   *   impact:<type>            → sum of impacts[] with that type
   *   comparison:<type>:<unit> → the comparisons[] entry matching type + unit
   * `[data-kelp-progress]` elements use the same grammar: width = value ÷ goal.
   */
  const startImpacts = () => {
    const nums = Array.from(root.querySelectorAll('[data-kelp-metric]'));
    const bars = Array.from(root.querySelectorAll('[data-kelp-progress]'));
    if (!nums.length && !bars.length) return;

    const fmt = (value, target) => {
      value = Math.max(0, value);
      if (target >= 1000000) {
        if (value < 1000) return Math.round(value).toLocaleString('en-US');
        if (value < 1000000) return `${(value / 1000).toFixed(0)}K`;
        const m = value / 1000000;
        return `${m >= 1 ? m.toFixed(2).replace(/\.?0+$/, '') : m.toFixed(1)}M`;
      }
      return Math.round(value).toLocaleString('en-US');
    };

    /* Reads el.kelpTarget every frame, so a fetch landing mid-count simply
       retargets the animation already in flight. */
    const animate = (el) => {
      if (reduced) {
        el.textContent = fmt(el.kelpTarget, el.kelpTarget);
        el.kelpDone = true;
        return;
      }
      if (el.kelpAnimating) return;
      el.kelpAnimating = true;

      let t0 = null;
      const duration = 1800;
      const ease = (t) => 1 - (1 - t) ** 3;

      const step = (now) => {
        if (t0 === null) t0 = now;
        const t = Math.min(1, (now - t0) / duration);
        el.textContent = fmt(el.kelpTarget * ease(t), el.kelpTarget);
        if (t < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.kelpAnimating = false;
          el.kelpDone = true;
        }
      };
      window.requestAnimationFrame(step);
    };

    /* Tween the shown figure from whatever it reads now to an arbitrary value —
       used by the finale's "See the goal" control to run up to 15,000. */
    const showTween = (el, value) => {
      const scale = Math.max(value, el.kelpTarget || 0);
      if (reduced) {
        el.textContent = fmt(value, scale);
        return;
      }
      el.kelpAnimating = true;
      let t0 = null;
      const duration = 1600;
      const ease = (t) => 1 - (1 - t) ** 3;
      const from = parseFloat((el.textContent || '0').replace(/[^0-9.]/g, '')) || 0;
      const step = (now) => {
        if (t0 === null) t0 = now;
        const t = Math.min(1, (now - t0) / duration);
        el.textContent = fmt(from + (value - from) * ease(t), scale);
        if (t < 1) window.requestAnimationFrame(step);
        else el.kelpAnimating = false;
      };
      window.requestAnimationFrame(step);
    };

    nums.forEach((el) => {
      el.kelpTarget = parseFloat(el.getAttribute('data-kelp-target')) || 0;
      el.kelpDone = false;
      el.kelpSeen = false;
      el.textContent = reduced ? fmt(el.kelpTarget, el.kelpTarget) : fmt(0, el.kelpTarget);
      /* Hooks the finale's Replay / See-the-goal controls call (startFinale). */
      el.kelpReplay = () => {
        el.kelpAnimating = false;
        el.kelpDone = false;
        el.textContent = fmt(0, el.kelpTarget);
        animate(el);
      };
      el.kelpShow = (value) => showTween(el, value);
    });

    if (reduced || !('IntersectionObserver' in window)) {
      nums.forEach((el) => {
        el.kelpSeen = true;
        animate(el);
      });
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.kelpSeen = true;
            animate(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.2 },
      );
      nums.forEach((el) => observer.observe(el));
    }

    const valueFor = (data, metric) => {
      const parts = (metric || '').split(':');
      if (parts[0] === 'impact' && Array.isArray(data.impacts)) {
        const hits = data.impacts.filter((it) => it && it.type === parts[1] && typeof it.amount === 'number');
        return hits.length ? hits.reduce((sum, it) => sum + it.amount, 0) : null;
      }
      if (parts[0] === 'comparison' && Array.isArray(data.comparisons)) {
        const hit = data.comparisons.find(
          (c) => c && c.type === parts[1] && c.unit === parts[2] && typeof c.amount === 'number',
        );
        return hit ? hit.amount : null;
      }
      return null;
    };

    const url = root.getAttribute('data-greenspark-url');
    const key = root.getAttribute('data-greenspark-key');
    if (!url || !key || typeof fetch !== 'function') return;

    fetch(url, { headers: { 'x-api-key': key } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        nums.forEach((el) => {
          const value = valueFor(data, el.getAttribute('data-kelp-metric'));
          if (value == null) return;
          el.kelpTarget = Math.floor(value); // whole units — no fractional kelp/bees/hives
          if (el.kelpDone) {
            el.textContent = fmt(el.kelpTarget, el.kelpTarget);
          } else if (!el.kelpSeen && !reduced) {
            el.textContent = fmt(0, el.kelpTarget);
          } else if (reduced) {
            el.textContent = fmt(el.kelpTarget, el.kelpTarget);
          }
        });
        bars.forEach((el) => {
          const value = valueFor(data, el.getAttribute('data-kelp-progress'));
          const goal = parseFloat(el.getAttribute('data-kelp-goal'));
          if (value == null || !(goal > 0)) return;
          el.style.width = `${Math.min(100, (value / goal) * 100).toFixed(2)}%`;
        });
      })
      .catch(() => {});
  };

  /**
   * The "Support the mission" CTAs point at the bundles by default; `?offer=device`
   * retargets them to the V3 Max card so campaigns can pick the destination.
   */
  const retargetCtas = () => {
    if (new URLSearchParams(window.location.search).get('offer') !== 'device') return;
    root.querySelectorAll('a[data-kelp-cta]').forEach((cta) => {
      cta.setAttribute('href', '#kelp-hero-device');
    });
  };

  /* One pulse on the leaf glyph now and then — the count is live, so say so. */
  const startLeafPulse = () => {
    if (!leaf || reduced) return;

    window.setInterval(() => {
      leaf.style.animation = 'none';
      void leaf.offsetWidth;
      leaf.style.animation = 'kelp-hero-leaf-pulse 0.9s ease';
    }, PULSE_INTERVAL);
  };

  if (lite) root.setAttribute('data-kelp-lite', '');
  if (forest) buildFronds();
  if (school) buildFish();
  if (motes) buildMotes();
  startFinale();
  startReveals();
  startRail();
  startChrome();
  startImpacts();
  startLeafPulse();
  retargetCtas();
})();
